import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFriends } from '../context/FriendContext';
import Icon from './Icon';

/* ── Profile dropdown ──────────────────────────────────────────── */
const ProfileDropdown = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity">
        {user.avatar
          ? <img src={user.avatar} alt="" className="w-7 h-7 rounded-full object-cover border border-zinc-700" />
          : <span className="w-7 h-7 rounded-full bg-f1red/20 border border-f1red/40 flex items-center justify-center text-xs font-black text-f1red">
              {user.username?.[0]?.toUpperCase()}
            </span>
        }
        <span className="text-sm font-semibold text-zinc-300 hidden lg:inline">{user.username}</span>
        <Icon name="chevronDown" size={12} className={`text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 bg-zinc-900 border border-zinc-700/80 rounded-xl shadow-2xl py-1.5 min-w-[170px] z-50">
          <Link to="/profile" onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors">
            <Icon name="user" size={15} />
            Mon profil
          </Link>
          <Link to="/my-driver" onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors">
            <Icon name="flag" size={15} />
            Mon pilote
          </Link>
          <div className="border-t border-zinc-800 my-1" />
          <button onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-400 hover:text-red-400 hover:bg-zinc-800 transition-colors">
            <Icon name="x" size={15} className="text-zinc-500" />
            Déconnexion
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Main Navbar ───────────────────────────────────────────────── */
const Navbar = () => {
  const { user, logout } = useAuth();
  const { unreadTotal = 0, pendingChallenges = [] } = useFriends() || {};
  const socialBadge = unreadTotal + pendingChallenges.length;
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/'); };

  const isBoxBoxActive = ['/box-box', '/lightspeed', '/quiz', '/podium', '/saison'].includes(location.pathname);

  const linkClass = ({ isActive }) =>
    `relative text-sm font-bold uppercase tracking-wider transition-colors pb-0.5 ${
      isActive
        ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-f1red'
        : 'text-zinc-400 hover:text-white'
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wide transition-colors ${
      isActive ? 'text-f1red bg-f1red/8' : 'text-zinc-300 hover:text-white hover:bg-zinc-800/60'
    }`;

  return (
    <nav className="bg-zinc-950 sticky top-0 z-50" style={{ boxShadow: '0 2px 0 #e10600' }}>
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">

        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0">
          <img
            src="/logo-P1-blanc.png"
            alt="P1 or Not P1"
            className="h-11 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-7 flex-1 justify-center">
          <NavLink to="/calendar" className={linkClass}>Calendrier</NavLink>
          <NavLink to="/standings" className={linkClass}>Standings</NavLink>

          <NavLink to="/paddock" className={linkClass}>
            <span className="relative inline-flex">
              Paddock
              {user && socialBadge > 0 && (
                <span className="absolute -top-2.5 -right-4 bg-f1red text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center leading-none">
                  {socialBadge > 9 ? '9+' : socialBadge}
                </span>
              )}
            </span>
          </NavLink>

          {/* Box-Box — simple NavLink like the others */}
          <NavLink to="/ecuries" className={linkClass}>Écuries</NavLink>

          <NavLink to="/box-box"
            className={`relative text-sm font-bold uppercase tracking-wider transition-colors pb-0.5 ${
              isBoxBoxActive
                ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-f1red'
                : 'text-zinc-400 hover:text-white'
            }`}>
            Box-Box
          </NavLink>
        </div>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          {user
            ? <ProfileDropdown user={user} onLogout={handleLogout} />
            : (
              <div className="flex items-center gap-2">
                <NavLink to="/login" className={linkClass}>Connexion</NavLink>
                <Link to="/register" className="btn-primary text-sm py-1.5 px-4">S'inscrire</Link>
              </div>
            )
          }
        </div>

        {/* Mobile right */}
        <div className="flex items-center gap-2 md:hidden">
          {user && socialBadge > 0 && (
            <Link to="/paddock?tab=amis" className="relative">
              <span className="bg-f1red text-white text-[9px] font-black px-1.5 py-0.5 rounded-full">{socialBadge}</span>
            </Link>
          )}
          <button className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors" onClick={() => setMenuOpen(o => !o)}>
            <Icon name={menuOpen ? 'x' : 'chart'} size={22} className="text-zinc-400" />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-zinc-800/60 px-3 py-3 flex flex-col gap-0.5 bg-zinc-950">

          <NavLink to="/calendar" className={mobileLinkClass}>
            <Icon name="calendar" size={16} />Calendrier
          </NavLink>
          <NavLink to="/standings" className={mobileLinkClass}>
            <Icon name="chart" size={16} />Standings
          </NavLink>
          <NavLink to="/paddock" className={({ isActive }) =>
            `flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wide transition-colors ${
              isActive ? 'text-f1red bg-f1red/8' : 'text-zinc-300 hover:text-white hover:bg-zinc-800/60'
            }`
          }>
            <span className="flex items-center gap-3">
              <Icon name="trophy" size={16} />Paddock
            </span>
            {user && socialBadge > 0 && (
              <span className="bg-f1red text-white text-[9px] font-black px-1.5 py-0.5 rounded-full">{socialBadge}</span>
            )}
          </NavLink>
          <NavLink to="/ecuries" className={mobileLinkClass}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            Écuries
          </NavLink>

          <NavLink to="/box-box" className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wide transition-colors ${
              isBoxBoxActive ? 'text-f1red bg-f1red/8' : 'text-zinc-300 hover:text-white hover:bg-zinc-800/60'
            }`
          }>
            <Icon name="lightning" size={16} />Box-Box
          </NavLink>

          {user ? (
            <>
              <div className="border-t border-zinc-800/60 my-2" />
              <NavLink to="/profile" className={mobileLinkClass}>
                <Icon name="user" size={16} />Mon profil
              </NavLink>
              <NavLink to="/my-driver" className={mobileLinkClass}>
                <Icon name="flag" size={16} />Mon pilote
              </NavLink>
              <button onClick={handleLogout}
                className="flex items-center gap-3 mt-1 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-wide text-red-400 hover:bg-red-900/20 transition-colors text-left">
                <Icon name="x" size={16} className="text-red-400" />Déconnexion
              </button>
            </>
          ) : (
            <>
              <div className="border-t border-zinc-800/60 my-2" />
              <NavLink to="/login" className={mobileLinkClass}>
                <Icon name="lock" size={16} />Connexion
              </NavLink>
              <Link to="/register" className="btn-primary text-sm py-2.5 px-3 text-center mt-1">
                S'inscrire
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
